import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from './AuthContext';
import { ADMIN_CATALOG_HYDRATED_EVENT, fetchAdminCatalogFromApi } from '../lib/adminCatalogApi';
import { applyDynamoCatalogToLocalStorage } from '../lib/adminCatalogHydration';

export type ReloadCatalogResult = { ok: true } | { ok: false; error: string };

interface AdminCatalogContextValue {
  catalogReady: boolean;
  catalogLoading: boolean;
  catalogError: string | null;
  catalogVersion: number;
  reloadCatalog: () => Promise<ReloadCatalogResult>;
}

const AdminCatalogContext = createContext<AdminCatalogContextValue | undefined>(undefined);

export function useAdminCatalog(): AdminCatalogContextValue {
  const ctx = useContext(AdminCatalogContext);
  if (!ctx) throw new Error('useAdminCatalog must be used within AdminCatalogProvider');
  return ctx;
}

/** Reexecuta quando o catálogo vindo da API for recarregado (ex.: «Tentar novamente» após login). */
export function useRefetchWhenCatalogChanges(effect: () => void): void {
  const { catalogVersion } = useAdminCatalog();
  const ref = useRef(effect);
  ref.current = effect;
  useEffect(() => {
    ref.current();
  }, [catalogVersion]);
}

export function AdminCatalogProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const everHydratedOk = useRef(false);
  const [catalogReady, setCatalogReady] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState<string | null>(null);
  const [catalogVersion, setCatalogVersion] = useState(0);

  const loadCatalog = useCallback(async (): Promise<ReloadCatalogResult> => {
    setCatalogLoading(true);
    setCatalogError(null);
    const r = await fetchAdminCatalogFromApi();
    if (r.ok) {
      applyDynamoCatalogToLocalStorage(r.data);
      everHydratedOk.current = true;
      setCatalogReady(true);
      setCatalogVersion((v) => v + 1);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(ADMIN_CATALOG_HYDRATED_EVENT));
      }
      setCatalogLoading(false);
      return { ok: true };
    }
    setCatalogError(r.error);
    if (!everHydratedOk.current) setCatalogReady(false);
    setCatalogLoading(false);
    return { ok: false, error: r.error };
  }, []);

  useEffect(() => {
    if (!user) {
      everHydratedOk.current = false;
      setCatalogReady(false);
      setCatalogError(null);
      setCatalogLoading(false);
      return;
    }
    void loadCatalog();
  }, [user?.userId, loadCatalog]);

  const reloadCatalog = useCallback(async () => loadCatalog(), [loadCatalog]);

  const value = useMemo(
    () => ({
      catalogReady,
      catalogLoading,
      catalogError,
      catalogVersion,
      reloadCatalog,
    }),
    [catalogReady, catalogLoading, catalogError, catalogVersion, reloadCatalog]
  );

  return <AdminCatalogContext.Provider value={value}>{children}</AdminCatalogContext.Provider>;
}
